import { createSlice } from "@reduxjs/toolkit";
import { portalApi } from "../api/portalApi";

// Re-export RTK Query hooks for direct component use
export {
  useGetSalesTargetsQuery,
  useCreateSalesTargetMutation,
  useUpdateSalesTargetMutation,
} from '../api/portalApi';

/**
 * Backward-compatible thunk wrappers so existing call-sites continue to work.
 */
export const fetchSalesTargetsData = () => (dispatch) => {
  dispatch(portalApi.endpoints.getSalesTargets.initiate(undefined, { forceRefetch: false }));
};

export const createSalesTargetRecord = (recordData) => (dispatch) => {
  return dispatch(portalApi.endpoints.createSalesTarget.initiate(recordData));
};

export const updateSalesTargetRecord = ({ id, field, value }) => (dispatch) => {
  return dispatch(portalApi.endpoints.updateSalesTarget.initiate({ id, field, value }));
};

const salesTargetsSlice = createSlice({
  name: "salesTargets",
  initialState: {
    data: [],
    loading: false,
    error: null,
    searchTerm: "",
    selectedRepType: "all",
    selectedYear: new Date().getFullYear(),
    allAccordionsOpen: false
  },
  reducers: {
    replaceRealtimeSalesTargets: (state, action) => { state.data = action.payload; },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSelectedRepType: (state, action) => {
      state.selectedRepType = action.payload;
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload;
    },
    setAllAccordionsOpen: (state, action) => {
      state.allAccordionsOpen = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // ── RTK Query: getSalesTargets ──────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.getSalesTargets.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.getSalesTargets.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.data = action.payload;
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.getSalesTargets.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error?.message ?? 'Failed to fetch sales targets';
        }
      )
      // ── RTK Query: createSalesTarget ────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.createSalesTarget.matchFulfilled,
        (state, action) => {
          state.loading = false;
          state.data.push(action.payload);
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.createSalesTarget.matchRejected,
        (state, action) => {
          state.loading = false;
          state.error = action.error?.message ?? 'Failed to create sales target';
        }
      )
      // ── RTK Query: updateSalesTarget ────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.updateSalesTarget.matchFulfilled,
        (state, action) => {
          const { id, field, value } = action.meta.arg.originalArgs;
          const recordIndex = state.data.findIndex(record => record.ID === id);
          if (recordIndex !== -1) {
            state.data[recordIndex][field] = value;
          }
          state.error = null;
        }
      )
      .addMatcher(
        portalApi.endpoints.updateSalesTarget.matchRejected,
        (state, action) => {
          state.error = action.error?.message ?? 'Failed to update sales target';
        }
      );
  }
});

export const {
  setSearchTerm,
  setSelectedRepType,
  setSelectedYear,
  setAllAccordionsOpen,
  clearError
} = salesTargetsSlice.actions;

// Selectors
export const selectSalesTargetsData = (state) => state.salesTargets.data;
export const selectSalesTargetsLoading = (state) => state.salesTargets.loading;
export const selectSalesTargetsError = (state) => state.salesTargets.error;
export const selectSearchTerm = (state) => state.salesTargets.searchTerm;
export const selectSelectedRepType = (state) => state.salesTargets.selectedRepType;
export const selectSelectedYear = (state) => state.salesTargets.selectedYear;
export const selectAllAccordionsOpen = (state) => state.salesTargets.allAccordionsOpen;

// Filtered data selector
export const selectFilteredSalesTargets = (state) => {
  const data = selectSalesTargetsData(state);
  const searchTerm = selectSearchTerm(state).toLowerCase();
  const selectedRepType = selectSelectedRepType(state);
  const selectedYear = selectSelectedYear(state);

  return data.filter(record => {
    const matchesSearch = !searchTerm || 
      record.SalesRep?.toLowerCase().includes(searchTerm) ||
      record.Market?.toLowerCase().includes(searchTerm);
    
    const matchesRepType = selectedRepType === "all" || 
      record.SalesRepType === selectedRepType;
    
    const matchesYear = record.Year === selectedYear;

    return matchesSearch && matchesRepType && matchesYear;
  });
};

// Grouped data selector for accordions
export const selectGroupedSalesTargets = (state) => {
  const filteredData = selectFilteredSalesTargets(state);
  
  const grouped = {};
  
  filteredData.forEach(record => {
    const key = `${record.Year}-${record.SalesRep}-${record.SalesRepType}`;
    if (!grouped[key]) {
      grouped[key] = {
        year: record.Year,
        salesRep: record.SalesRep,
        salesRepType: record.SalesRepType,
        records: []
      };
    }
    grouped[key].records.push(record);
  });
  
  // Sort groups alphabetically by sales rep name, then by year (descending)
  return Object.values(grouped).sort((a, b) => {
    // First sort by sales rep name alphabetically
    const repComparison = a.salesRep.localeCompare(b.salesRep);
    if (repComparison !== 0) {
      return repComparison;
    }
    // If same rep, sort by year descending (newest first)
    return b.year - a.year;
  });
};

export default salesTargetsSlice.reducer;

export const { replaceRealtimeSalesTargets } = salesTargetsSlice.actions;
